import classNames from 'classnames';
import DataController from 'components/DataController/DataController';
import ImgUploaderByEntityId from 'components/ImgUploader/ImgUploaderByEntityId';
import PricesList from 'components/Lists/ListView/PricesList';
import ChunkOfTextWithCopying from 'components/Text/ChunkOfTextWithCopying';
import RestaurantsList from 'components/Lists/ListView/RestaurantsList';
import Text from 'components/Text/Text';
import { RequestEntity, UserRoles, IItemDishesForPagination, TDishesSelectedItem } from 'types';
import { useRoleAccess } from 'hooks/useRoleAccess';
import React, { useState } from 'react';
import CategoriesList from './components/CategoriesList';
import Info from './components/Info/Info';
import SubCategoriesList from './components/SubCategoriesList';
import style from './Item.module.scss';

interface IItem {
	data: IItemDishesForPagination;
	index: number;
	IDSelectedRestaurant: TDishesSelectedItem;
	setDishIDForDeletion: (dishID: string) => void;
	setDishIDForFullInfo: (dishID: string) => void;
}

const Item: React.FC<IItem> = ({ data, index, IDSelectedRestaurant, setDishIDForDeletion, setDishIDForFullInfo }) => {
	const [isOpenInfo, setIsOpenInfo] = useState<boolean>(false);
	const hasDeleteAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);

	const isUnAssigned =
		!data.subCategories.some((el) => el.restaurantId === IDSelectedRestaurant) && !!IDSelectedRestaurant;
	const isDisabledDish = data.restaurantsDishes.filter(
		(el) => el.isDisabled && el.restaurantId === IDSelectedRestaurant,
	).length;

	const containerClasses = classNames(style.container, {
		[style.openInfo]: isOpenInfo,
		[style.itemUnAssigned]: isUnAssigned,
	});
	const nameRowClasses = classNames(style.row, style.name);
	const imgWrapperClass = classNames(style.imgWrapper, { [style.itemDisabled]: isDisabledDish });
	const dishNameClasses = classNames(style.dishName);
	const articleRowClasses = classNames(style.article, style.row);
	const categoryRowClasses = classNames(style.category, style.row, style.list);
	const subCategoryRowClasses = classNames(style.subCategory, style.row, style.list);
	const restaurantRowClasses = classNames(style.restaurant, style.row, style.list);
	const isDisabledRowClasses = classNames(style.isDisabled, style.row, style.list);
	const priceRowClasses = classNames(style.price, style.row, style.list);
	const externalIdRowClasses = classNames(style.externalId, style.row);
	const actionRowClasses = classNames(style.action, style.row);

	const restaurants = data.restaurantsDishes.map((el) => ({
		...el,
		id: el.restaurantId,
	}));
	const isDisabledInRestaurants = restaurants.filter((el) => el.isDisabled);
	const selectedRestaurant = restaurants.find((el) => el.restaurantId === IDSelectedRestaurant);

	const handleClickInfo = () => {
		setIsOpenInfo(!isOpenInfo);
	};

	const handleSetIdForDeletion = () => {
		setDishIDForDeletion(data.id);
	};

	return (
		<div data-test={`Item-${index}`}>
			<div className={containerClasses}>
				<div className={nameRowClasses}>
					<div className={imgWrapperClass}>
						<ImgUploaderByEntityId
							resource={RequestEntity.Dishes}
							entityID={data.filePath ? data.id : ''}
							photoSize="Small"
							loaderSize="small"
							filePath={data.filePath}
						/>
					</div>
					<Text className={dishNameClasses} translationKey={data.name} withTooltip={true} />
				</div>
				<Text
					className={articleRowClasses}
					translationNameSpace="listing"
					translationKey={data.articleNumber}
					withTooltip={true}
				/>
				<div className={categoryRowClasses}>
					{isUnAssigned ? '-' : <CategoriesList data={data.categories} />}
				</div>
				<div className={subCategoryRowClasses}>
					{isUnAssigned ? '-' : <SubCategoriesList data={data.allSubCategories} />}
				</div>
				<div className={restaurantRowClasses} data-test="restaurantsDishes">
					<RestaurantsList restaurants={restaurants} />
				</div>
				<div className={isDisabledRowClasses} data-test="isDisabledInRestaurants">
					<RestaurantsList restaurants={isDisabledInRestaurants} />
				</div>
				<div className={priceRowClasses}>
					<PricesList restaurants={restaurants} selectedRestaurant={selectedRestaurant} />
				</div>
				<div className={externalIdRowClasses}>
					{data.externalId && <ChunkOfTextWithCopying text={data.externalId} />}
				</div>
				<div className={actionRowClasses}>
					<DataController
						onClickInfo={handleClickInfo}
						onClickDelete={hasDeleteAccess ? handleSetIdForDeletion : undefined}
						URLWithIDToEdit={`/menu/dishes/edit?dishID=${data.id}`}
						size="smallBtn"
						isOpenInfo={isOpenInfo}
					/>
				</div>
			</div>
			<Info
				isOpenInfo={isOpenInfo}
				isUnAssigned={isUnAssigned}
				data={data}
				setDishIDForFullInfo={setDishIDForFullInfo}
			/>
		</div>
	);
};

export default React.memo(Item);
